import React, { useState, useEffect } from 'react';
import API from '../api';

export default function Admin() {
  const [users, setUsers]         = useState([]);
  const [earlyAccess, setEarlyAccess] = useState([]);
  const [clips, setClips]         = useState({ total:0, posted:0, pending:0 });
  const [loading, setLoading]     = useState(true);
  const [tab, setTab]             = useState('users');

  useEffect(() => {
    Promise.all([
      API.get('/api/admin/users'),
      API.get('/api/admin/stats'),
      API.get('/api/admin/early-access'),
    ]).then(([u, s, ea]) => {
      setUsers(u.data);
      setClips(s.data);
      setEarlyAccess(ea.data);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  const setUserStatus = async (userId, status) => {
    await API.post(`/api/admin/users/${userId}/stripe-status`, { status });
    const res = await API.get('/api/admin/users');
    setUsers(res.data);
  };

  const fmt = dt => dt ? new Date(dt).toLocaleDateString('en-GB', { day:'numeric', month:'short', year:'numeric' }) : '—';

  const statusBadge = (status) => {
    const colours = {
      active:    { bg:'var(--accent-dim)', color:'var(--accent)' },
      trialing:  { bg:'rgba(91,156,246,0.1)', color:'#5b9cf6' },
      cancelled: { bg:'rgba(255,68,68,0.08)', color:'#ff4444' },
      canceled:  { bg:'rgba(255,68,68,0.08)', color:'#ff4444' },
      none:      { bg:'var(--surface2)', color:'var(--text-muted)' },
    };
    const c = colours[status] || colours.none;
    return (
      <span style={{...c, fontSize:11, fontWeight:600, padding:'2px 8px', borderRadius:4, textTransform:'uppercase', letterSpacing:'0.05em'}}>
        {status || 'none'}
      </span>
    );
  };

  return (
    <div>
      <div className="page-header">
        <div className="page-title">Admin</div>
        <div className="page-subtitle">Manage users, subscriptions and early access</div>
      </div>

      {/* Stats row */}
      <div className="stats-row" style={{marginBottom:28}}>
        <div className="stat-card"><div className="stat-label">Total users</div><div className="stat-value">{users.length}</div></div>
        <div className="stat-card"><div className="stat-label">Active / trialing</div><div className="stat-value">{users.filter(u=>['active','trialing'].includes(u.stripe_status)).length}</div></div>
        <div className="stat-card"><div className="stat-label">Early access signups</div><div className="stat-value">{earlyAccess.length}</div></div>
        <div className="stat-card"><div className="stat-label">Total clips posted</div><div className="stat-value">{clips.posted}</div></div>
      </div>

      {/* Tabs */}
      <div className="filter-tabs" style={{marginBottom:20}}>
        {['users','early-access'].map(t => (
          <button key={t} className={`filter-tab${tab===t?' active':''}`} onClick={()=>setTab(t)}>
            {t === 'early-access' ? 'Early Access' : 'Users'}
          </button>
        ))}
      </div>

      {loading ? (
        <div style={{color:'var(--text-muted)', fontSize:14}}>Loading...</div>
      ) : tab === 'users' ? (
        <div style={{background:'var(--surface)', border:'1px solid var(--border)', borderRadius:12, overflow:'hidden'}}>
          <table style={{width:'100%', borderCollapse:'collapse', fontSize:13}}>
            <thead>
              <tr style={{borderBottom:'1px solid var(--border)'}}>
                {['Name','Email','Signed up','Subscription','Trial ends','Actions'].map(h => (
                  <th key={h} style={{padding:'12px 16px', textAlign:'left', fontSize:11, fontWeight:600, textTransform:'uppercase', letterSpacing:'0.08em', color:'var(--text-muted)'}}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {users.map(u => (
                <tr key={u.id} style={{borderBottom:'1px solid var(--border)'}}>
                  <td style={{padding:'14px 16px', fontWeight:600}}>{u.display_name || '—'}</td>
                  <td style={{padding:'14px 16px', color:'var(--text-dim)'}}>{u.email}</td>
                  <td style={{padding:'14px 16px', color:'var(--text-muted)'}}>{fmt(u.created_at)}</td>
                  <td style={{padding:'14px 16px'}}>{statusBadge(u.stripe_status)}</td>
                  <td style={{padding:'14px 16px', color:'var(--text-muted)'}}>{fmt(u.stripe_trial_end)}</td>
                  <td style={{padding:'14px 16px'}}>
                    <div style={{display:'flex', gap:6}}>
                      {u.stripe_status !== 'active' && (
                        <button
                          className="btn btn-approve"
                          style={{fontSize:11, padding:'4px 10px'}}
                          onClick={() => setUserStatus(u.id, 'active')}
                        >
                          Activate
                        </button>
                      )}
                      {u.stripe_status === 'active' && (
                        <button
                          className="btn btn-ghost"
                          style={{fontSize:11, padding:'4px 10px'}}
                          onClick={() => setUserStatus(u.id, 'cancelled')}
                        >
                          Cancel
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div style={{background:'var(--surface)', border:'1px solid var(--border)', borderRadius:12, overflow:'hidden'}}>
          <table style={{width:'100%', borderCollapse:'collapse', fontSize:13}}>
            <thead>
              <tr style={{borderBottom:'1px solid var(--border)'}}>
                {['Email','Signed up'].map(h => (
                  <th key={h} style={{padding:'12px 16px', textAlign:'left', fontSize:11, fontWeight:600, textTransform:'uppercase', letterSpacing:'0.08em', color:'var(--text-muted)'}}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {earlyAccess.map((e, i) => (
                <tr key={i} style={{borderBottom:'1px solid var(--border)'}}>
                  <td style={{padding:'14px 16px'}}>{e.email}</td>
                  <td style={{padding:'14px 16px', color:'var(--text-muted)'}}>{fmt(e.created_at)}</td>
                </tr>
              ))}
              {earlyAccess.length === 0 && (
                <tr><td colSpan={2} style={{padding:'24px 16px', color:'var(--text-muted)', textAlign:'center'}}>No early access signups yet</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
